import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faSpinner, faPaperPlane, faCircleInfo, faClockRotateLeft } from '@fortawesome/free-solid-svg-icons';
import api from '../utils/api';
import RichTextEditor from './RichTextEditor';
import DatePickerField from './DatePickerField';
import PersonPicker from './PersonPicker';
import RecentOptions from '../components/RecentOptions';
import { recordRecentPick } from '../utils/recentPicks';
import TaskTimeWindow from './TaskTimeWindow';
import { addDays, datesBetween, prettyDate, todayYmd } from '../utils/scheduleDates';
import '../styles/recurring.css';
import '../styles/selftask.css';
import '../styles/attachmentRequirement.css';

// How far back work can be logged (server/utils/backdatedTasks.js).
const BACKDATE_WINDOW_DAYS = 30;

/**
 * An employee logging work they were handed verbally (TaskPlan.md §15).
 *
 * A dialog rather than a page: this is a short form reached from the board, and
 * sending someone to a separate route to fill in six fields would lose their
 * place. Doubles as the resubmit form for a rejected request — same fields, so
 * the same component, with `task` supplied.
 *
 * "I've already done this" switches it to back-dated work: one past day
 * instead of a start and due date, saved as already Completed on that day
 * (server/utils/backdatedTasks.js). `backdatedDay` opens it that way, on that
 * day.
 */
const SelfTaskForm = ({ open, onClose, onSaved, task = null, prefill = null, backdatedDay = null }) => {
    const isResubmit = Boolean(task);
    const [backdated, setBackdated] = useState(false);
    const [workDate, setWorkDate] = useState('');
    const earliest = addDays(todayYmd(), -BACKDATE_WINDOW_DAYS);

    const [saving, setSaving] = useState(false);
    const [people, setPeople] = useState([]);
    const [projects, setProjects] = useState([]);
    const [form, setForm] = useState({
        title: '', description: '', taskType: 'Regular Office Task',
        projectId: '', priority: 'Medium', assignedById: '',
        startTime: '', dueTime: '', timeAllottedMinutes: ''
    });
    const [dates, setDates] = useState([]);

    useEffect(() => {
        if (!open) return;

        // Only people who can approve this request: managers, HR, admins, and
        // team leads in this employee's own chain. Naming anyone else sent the
        // request to someone with no power to act on it.
        api.get('/tasks/self-approvers')
            .then(res => setPeople(res.data || []))
            .catch(() => setPeople([]));

        // Employees can't reach the management project list, so a failure here
        // just means the project option stays unavailable to them.
        api.get('/projects')
            .then(res => setProjects(res.data || []))
            .catch(() => setProjects([]));
    }, [open]);

    // Reopening for a different task must not show the previous one's values.
    useEffect(() => {
        if (!open) return;
        if (task) {
            setForm({
                title: task.title || '',
                description: task.description || '',
                taskType: task.taskType || 'Regular Office Task',
                projectId: task.projectId?._id || task.projectId || '',
                priority: task.priority || 'Medium',
                assignedById: task.assignedBy?._id || task.assignedBy || '',
                startTime: task.startTime || '',
                dueTime: task.dueTime || '',
                timeAllottedMinutes: task.timeAllottedMinutes || ''
            });
            const start = task.startDate ? task.startDate.slice(0, 10) : null;
            const due = task.dueDate ? task.dueDate.slice(0, 10) : null;
            setDates(due ? datesBetween(start || due, due) : []);
            setBackdated(Boolean(task.isBackdated));
            setWorkDate(task.workDate || '');
        } else {
            // `prefill` starts a new request from something else - a meeting's
            // follow-up - with what is already known filled in.
            setForm({
                title: prefill?.title || '', description: prefill?.description || '',
                taskType: prefill?.projectId ? 'Project Task' : 'Regular Office Task',
                projectId: prefill?.projectId || '', priority: 'Medium', assignedById: '',
                startTime: '', dueTime: '', timeAllottedMinutes: ''
            });
            setDates([]);
            setBackdated(Boolean(backdatedDay));
            setWorkDate(backdatedDay || '');
        }
    }, [open, task, prefill, backdatedDay]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === 'Escape' && !saving) onClose(); };
        document.addEventListener('keydown', onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = prev;
        };
    }, [open, saving, onClose]);

    if (!open) return null;

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const dateLabel = () => {
        if (dates.length === 0) return 'Pick the start and due date';
        if (dates.length === 1) return prettyDate(dates[0]);
        return `${prettyDate(dates[0])} → ${prettyDate(dates[dates.length - 1])}`;
    };

    const submit = async (e) => {
        e.preventDefault();

        if (!form.title.trim()) return Swal.fire('Add a title', 'Say what the work is.', 'warning');
        if (!form.assignedById) return Swal.fire('Who asked you?', 'Pick the person who gave you this work.', 'warning');
        if (form.taskType === 'Project Task' && !form.projectId) {
            return Swal.fire('Pick a project', 'Choose a project, or switch this to a Regular Office task.', 'warning');
        }
        if (backdated && !workDate) return Swal.fire('Pick the day', 'Choose the day you did this work.', 'warning');
        if (!backdated && dates.length === 0) return Swal.fire('Pick the dates', 'Choose when this starts and when it is due.', 'warning');
        if (form.startTime && form.dueTime && form.dueTime <= form.startTime) {
            return Swal.fire('Check times', 'The due/end time must be after the start time.', 'warning');
        }

        const payload = backdated
            ? { ...form, backdated: true, workDate }
            : { ...form, startDate: dates[0], dueDate: dates[dates.length - 1] };

        setSaving(true);
        try {
            if (isResubmit) {
                await api.put(`/tasks/self/${task._id}`, payload);
            } else {
                await api.post('/tasks/self', payload);
            }
            await Swal.fire({
                icon: 'success',
                title: isResubmit ? 'Sent back for approval' : backdated ? 'Work logged' : 'Task logged',
                text: backdated
                    ? `Logged as done on ${prettyDate(workDate)}. Your approver has been notified.`
                    : 'Your manager has been notified. You can start on it right away.',
                timer: 2400,
                showConfirmButton: false
            });
            onSaved?.();
            onClose();
        } catch (err) {
            Swal.fire('Could not save', err.response?.data?.message || 'Please try again.', 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="st-overlay" onMouseDown={(e) => e.target === e.currentTarget && !saving && onClose()}>
            <div className="st-dialog" role="dialog" aria-modal="true" aria-label="Log a task">
                <header className="st-head">
                    <div>
                        <h3 className="st-title">
                            {isResubmit
                                ? (backdated ? 'Edit back-dated work' : 'Edit and resubmit')
                                : (backdated ? 'Log work you have already done' : 'Log a task for yourself')}
                        </h3>
                        <p className="st-subtitle">
                            {backdated
                                ? 'For work done on an earlier day that was not logged at the time. It is saved as done on that day and needs approval.'
                                : isResubmit
                                ? 'Fix what was flagged and send it back for approval.'
                                : 'For work you were handed directly. It needs your manager’s approval, but you can start straight away.'}
                        </p>
                    </div>
                    <button type="button" className="st-close" onClick={onClose} disabled={saving} aria-label="Close">
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                </header>

                <form onSubmit={submit}>
                    <div className="st-body">
                        {/* Left: what the work actually is. */}
                        <div className="st-col left">
                            <div className="form-group">
                                <label className="input-label">{backdated ? 'What did you do? *' : 'What needs doing? *'}</label>
                                <input
                                    className="custom-input" type="text" value={form.title}
                                    onChange={(e) => set('title', e.target.value)}
                                    placeholder={backdated ? 'e.g. Visited the Spectra site for the install' : 'e.g. Rework the Spectra pitch deck'}
                                    autoFocus
                                />
                            </div>

                            <div className="form-group st-details">
                                <label className="input-label">Details</label>
                                <RichTextEditor
                                    rows={4}
                                    value={form.description}
                                    onChange={(v) => set('description', v)}
                                    placeholder={'Anything worth recording about what was asked for \u2014 what was agreed, what done looks like.'}
                                />
                            </div>
                        </div>

                        {/* Right: everything about it. */}
                        <div className="st-col">
                            <div className="form-group">
                                <label className="input-label">Who asked you to do this? *</label>
                                <PersonPicker
                                    people={people}
                                    value={form.assignedById}
                                    onChange={(id) => set('assignedById', id)}
                                    recentKey="self-task-requester"
                                    placeholder="Search for a person..."
                                />
                                <div className="text-small text-muted" style={{ marginTop: 4 }}>
                                    Only people who can approve this task are listed.
                                </div>
                            </div>

                            <div className="st-pair">
                                <div className="form-group">
                                    <label className="input-label">Priority</label>
                                    <select
                                        className="swal2-select custom-select"
                                        value={form.priority}
                                        onChange={(e) => set('priority', e.target.value)}
                                    >
                                        <option value="Low">Low</option>
                                        <option value="Medium">Medium</option>
                                        <option value="High">High</option>
                                        <option value="Urgent">Urgent</option>
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label className="input-label">Type</label>
                                    <select
                                        className="swal2-select custom-select"
                                        value={form.taskType}
                                        onChange={(e) => {
                                            set('taskType', e.target.value);
                                            if (e.target.value === 'Regular Office Task') set('projectId', '');
                                        }}
                                    >
                                        <option value="Regular Office Task">Regular Office</option>
                                        <option value="Project Task">Project Task</option>
                                    </select>
                                </div>
                            </div>

                            {form.taskType === 'Project Task' && (
                                <div className="form-group">
                                    <label className="input-label">Project *</label>
                                    <select
                                        className="swal2-select custom-select"
                                        value={form.projectId}
                                        onChange={(e) => { set('projectId', e.target.value); recordRecentPick('project', e.target.value); }}
                                    >
                                        <option value="">Select a project</option>
                                        <RecentOptions kind="project" items={projects} />
                                    </select>
                                    {projects.length === 0 && (
                                        <small className="task-hint">
                                            No projects available to you \u2014 log it as Regular Office instead.
                                        </small>
                                    )}
                                </div>
                            )}

                            {!isResubmit && (
                                <div className="form-group">
                                    <button
                                        type="button"
                                        className={`atr-toggle ${backdated ? 'is-on' : ''}`}
                                        onClick={() => setBackdated(v => !v)}
                                        aria-pressed={backdated}
                                    >
                                        <span className="atr-icon"><FontAwesomeIcon icon={faClockRotateLeft} /></span>
                                        <span className="atr-text">
                                            <span className="atr-label">I have already done this</span>
                                            <span className="atr-hint">
                                                {backdated
                                                    ? `Back-dated: saved as done on the day you pick (up to ${BACKDATE_WINDOW_DAYS} days ago).`
                                                    : 'Turn on to log work from an earlier day.'}
                                            </span>
                                        </span>
                                        <span className="atr-switch" aria-hidden="true"><span className="atr-knob" /></span>
                                    </button>
                                </div>
                            )}

                            {backdated ? (
                                <div className="form-group">
                                    <label className="input-label">Day you did this *</label>
                                    <DatePickerField
                                        displayValue={workDate ? prettyDate(workDate) : 'Pick the day'}
                                        isEmpty={!workDate}
                                        mode="single"
                                        selected={workDate ? [workDate] : []}
                                        onChange={(days) => setWorkDate(days[days.length - 1] || '')}
                                        minDate={earliest}
                                        maxDate={todayYmd()}
                                    />
                                </div>
                            ) : (
                                <div className="form-group">
                                    <label className="input-label">Timeline *</label>
                                    <DatePickerField
                                        displayValue={dateLabel()}
                                        isEmpty={dates.length === 0}
                                        mode="range"
                                        selected={dates}
                                        onChange={setDates}
                                        minDate={todayYmd()}
                                    />
                                </div>
                            )}

                            <div className="form-group">
                                <TaskTimeWindow
                                    startTime={form.startTime}
                                    dueTime={form.dueTime}
                                    timeAllottedMinutes={form.timeAllottedMinutes}
                                    onChange={(patch) => setForm(f => ({ ...f, ...patch }))}
                                />
                            </div>
                        </div>
                    </div>

                    <footer className="st-foot">
                        <p className="st-note">
                            <FontAwesomeIcon icon={faCircleInfo} />
                            {/* One span, not loose text: .st-note is a flex row, so every bare
                                text node and every <strong> would become its own flex item and
                                the sentence would break into columns. */}
                            {backdated ? (
                                <span>
                                    You can change or remove it until it is <strong>approved</strong>. After
                                    that only Admin, HR or your team lead can change it.
                                </span>
                            ) : (
                                <span>
                                    Your reporting manager, team lead and HR can approve this. It shows on
                                    your board as <strong>Awaiting approval</strong> until one of them does.
                                </span>
                            )}
                        </p>
                        <div className="st-foot-actions">
                            <button type="button" className="gts-btn secondary" onClick={onClose} disabled={saving}>
                                Cancel
                            </button>
                            <button type="submit" className="gts-btn primary" disabled={saving}>
                                {saving
                                    ? <><FontAwesomeIcon icon={faSpinner} spin /> Saving...</>
                                    : <><FontAwesomeIcon icon={faPaperPlane} /> {isResubmit ? (backdated ? 'Save changes' : 'Resubmit') : 'Send for approval'}</>}
                            </button>
                        </div>
                    </footer>
                </form>
            </div>
        </div>
    );
};

export default SelfTaskForm;
